import { useCallback, useEffect, useState, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { TFunction } from 'i18next';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { LoginForm } from '../ui/login-form';
import { withErrorHandling } from '@selfserve/react-hocs';
import { storage, storageKeys, useAuth, useStorage } from '@selfserve/react-hooks';
import {
  StyledContentContainer,
  StyledTitle,
  CommonLayout,
} from '@selfserve/react-ui';
import { routesPaths, getRecaptchaToken, responseTypes } from '@selfserve/react-utils';

import { useLoginRequest, useLoginByEmailRequest } from '../data';

const StyledLoginContainer = styled.div`
  max-width: 584px;
  width: 100%;
`;

const StyledLoginTitle = styled(StyledTitle)`
  display: block;
  margin-bottom: 32px;
  text-align: center;
`;

const validationSchema = (t: TFunction, loginWithEmail: boolean) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('common.validations.email-valid'))
      .required(t('common.validations.email-required')),
    ...(!loginWithEmail && {
      password: Yup.string().required(t('common.validations.password-required')),
    }),
  });

export interface FormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

export const Login = withErrorHandling(({ openErrorModal }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setToken } = useAuth();
  const [, setSessionEmail] = useStorage(storage.SESSION, storageKeys.EMAIL);
  const [loginWithEmail, setLoginWithEmail] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);
  const {
    data: loginResponse,
    mutateAsync: handleLoginRequest,
    isLoading: isLoginLoading,
    isSuccess: isLoginSuccess,
  } = useLoginRequest();
  const {
    data: loginByEmailResponse,
    mutateAsync: handleLoginByEmailRequest,
    isLoading: isLoginByEmailLoading,
    variables: loginByEmailVariables,
  } = useLoginByEmailRequest();
  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    validateOnChange,
    initialValues: { email: '', password: '', rememberMe: false },
    validationSchema: validationSchema(t, loginWithEmail),
    onSubmit: ({ email, password }) => {
      if (!isLoginLoading && !isLoginByEmailLoading) {
        if (loginWithEmail) {
          getRecaptchaToken().then((recaptchaToken: string) => {
            handleLoginByEmailRequest({ email, recaptchaToken });
          })
        } else {
          handleLoginRequest({ email, password });
        }
      }
    },
  });
  const { errors, values } = formik;

  const handleToggleLoginByEmail = useCallback(() => setLoginWithEmail(value => !value), []);

  const handleLinkClick = useCallback(
    (link: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (link) {
        if (link.includes('http')) {
          window.open(link, '_blank');
        } else {
          navigate(link);
        }
      }
    },
    [navigate]
  );

  useEffect(() => {
    const hasError = !!Object.keys(errors).length;
    if (hasError && !validateOnChange) {
      setValidateOnChange(true);
    } else if (!hasError) {
      setValidateOnChange(false);
    }
  }, [validateOnChange, errors]);

  useEffect(() => {
    if (isLoginSuccess) {
      const { status, data } = loginResponse || {};
      if (String(status).startsWith(responseTypes.SUCCESS)) {
        setToken(data?.access_token as string, values.rememberMe);
        navigate(routesPaths.MANAGEMENT_OVERVIEW);
      }
      if (String(status).startsWith(responseTypes.CLIENT_ERROR)) {
        const { id } = data || {};
        if (id && i18n.exists(`errors.${id}`)) {
          const text = t(`errors.${id}`);
          const title = i18n.exists(`error-titles.${id}`)
            ? t(`error-titles.${id}`)
            : '';
          openErrorModal({ text, title });
        } else {
          openErrorModal();
        }
      }
    }
  }, [loginResponse, navigate]);

  useEffect(() => {
    const { status } = loginByEmailResponse || {};
    if (String(status).startsWith(responseTypes.SUCCESS)) {
      setSessionEmail(loginByEmailVariables?.email);
      navigate(routesPaths.CHECK_YOUR_INBOX);
    }
    if (String(status).startsWith(responseTypes.CLIENT_ERROR)) {
      openErrorModal();
    }
  }, [loginByEmailResponse]);

  return (
    <>
      <Helmet>
        <title>{t('titles.login')}</title>
      </Helmet>
      <CommonLayout showSideImage>
        <StyledContentContainer centered>
          <StyledLoginContainer>
            <StyledLoginTitle className="signHeaderText">
              {t('common.login')}
            </StyledLoginTitle>
            <LoginForm
              formik={formik}
              isLoading={isLoginLoading || isLoginByEmailLoading}
              handleLinkClick={handleLinkClick}
              loginWithEmail={loginWithEmail}
              handleToggleLoginByEmail={handleToggleLoginByEmail}
            />
          </StyledLoginContainer>
        </StyledContentContainer>
      </CommonLayout>
    </>
  );
});
